import React, { useState, useMemo } from 'react';
import Head from 'next/head';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Link from 'next/link';
import { xlimFeatures, supportStatusLegend, getCategories, getStatistics } from '../data/xlimFeatures';

export default function XlimSupport() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});

  const categories = getCategories();
  const stats = getStatistics();

  const filteredFeatures = useMemo(() => {
    return xlimFeatures.filter(feature => {
      const matchesSearch = searchQuery === '' || 
        feature.feature.toLowerCase().includes(searchQuery.toLowerCase()) ||
        feature.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        feature.subcategory.toLowerCase().includes(searchQuery.toLowerCase()) ||
        feature.notes.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || feature.category === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || feature.status === selectedStatus;
      const isNotEnhanced = feature.status !== 'enhanced';
      
      return matchesSearch && matchesCategory && matchesStatus && isNotEnhanced;
    });
  }, [searchQuery, selectedCategory, selectedStatus]);

  const groupedFeatures = useMemo(() => {
    const grouped = {};
    filteredFeatures.forEach(feature => {
      if (!grouped[feature.category]) {
        grouped[feature.category] = {};
      }
      if (!grouped[feature.category][feature.subcategory]) {
        grouped[feature.category][feature.subcategory] = [];
      }
      grouped[feature.category][feature.subcategory].push(feature);
    });
    return grouped;
  }, [filteredFeatures]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    Object.keys(groupedFeatures).forEach(cat => {
      allExpanded[cat] = true;
    });
    setExpandedCategories(allExpanded);
  };

  const collapseAll = () => {
    setExpandedCategories({});
  };

  const getStatusBadge = (status) => {
    const config = {
      supported: { bg: 'bg-green-100', text: 'text-green-800', label: '✔ Supported' },
      enhanced: { bg: 'bg-blue-100', text: 'text-blue-800', label: '✔+ Enhanced' },
      partial: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '✔- Partial' },
      notSupported: { bg: 'bg-red-100', text: 'text-red-800', label: '✗ Not Supported' }
    };
    const { bg, text, label } = config[status] || config.notSupported;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
        {label}
      </span>
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all';

  return (
    <>
      <Head>
        <title>XLIM Feature Support | Personalia - InDesign Feature Compatibility</title>
        <meta name="description" content="Explore the comprehensive list of Adobe InDesign features supported by XLIM. Search and filter to find specific feature compatibility information." />
      </Head>

      <Header />

      <main id="main-content">
        {/* Hero Section */}
        <section className="bg-gray-900 text-white py-16 md:py-20">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                XLIM Feature Support
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Explore the comprehensive list of Adobe InDesign features supported by XLIM. 
                Use the search and filters below to find specific feature compatibility.
              </p>
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="py-8 bg-white border-b">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-gray-900">{stats.total - stats.enhanced}</div>
                <div className="text-sm text-gray-600">Total Features</div>
              </div>
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-green-600">{stats.supported}</div>
                <div className="text-sm text-gray-600">Fully Supported</div>
              </div>
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-yellow-600">{stats.partial}</div>
                <div className="text-sm text-gray-600">Partial Support</div>
              </div>
              <div className="text-center p-4">
                <div className="text-3xl font-bold text-red-600">{stats.notSupported}</div>
                <div className="text-sm text-gray-600">Not Supported</div>
              </div>
            </div>
          </div>
        </section>

        {/* Legend Section */}
        <section className="py-6 bg-gray-50 border-b">
          <div className="container-custom">
            <div className="flex flex-wrap justify-center gap-4 md:gap-8">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">✔ Supported</span>
                <span className="text-sm text-gray-600">Fully supported</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">✔- Partial</span>
                <span className="text-sm text-gray-600">Some limitations</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">✗ Not Supported</span>
                <span className="text-sm text-gray-600">Not available</span>
              </div>
            </div>
          </div>
        </section>

        {/* Search and Filters Section */}
        <section className="py-8 bg-white sticky top-0 z-10 shadow-sm">
          <div className="container-custom">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search features, categories, or notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>

                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white"
                >
                  <option value="all">All Statuses</option>
                  <option value="supported">✔ Supported</option>
                  <option value="partial">✔- Partial</option>
                  <option value="notSupported">✗ Not Supported</option>
                </select>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-3 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Results count and expand/collapse */}
            <div className="flex justify-between items-center mt-4">
              <p className="text-sm text-gray-600">
                Showing <span className="font-semibold">{filteredFeatures.length}</span> of {xlimFeatures.length - stats.enhanced} features
              </p>
              <div className="flex gap-2">
                <button
                  onClick={expandAll}
                  className="text-sm text-primary hover:text-primary-dark"
                >
                  Expand All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={collapseAll}
                  className="text-sm text-primary hover:text-primary-dark"
                >
                  Collapse All
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Table Section */}
        <section className="py-8 bg-gray-50">
          <div className="container-custom">
            {Object.keys(groupedFeatures).length === 0 ? (
              <div className="text-center py-16">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No features found</h3>
                <p className="mt-2 text-gray-500">Try adjusting your search or filter criteria.</p>
                <button
                  onClick={clearFilters}
                  className="mt-4 btn btn-primary"
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedFeatures).map(([category, subcategories]) => (
                  <div key={category} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full px-6 py-4 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          className={`h-5 w-5 text-gray-500 transition-transform ${expandedCategories[category] ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <h2 className="text-lg font-semibold text-gray-900">{category}</h2>
                      </div>
                      <span className="text-sm text-gray-500">
                        {Object.values(subcategories).flat().length} features
                      </span>
                    </button>

                    {/* Category Content */}
                    {expandedCategories[category] && (
                      <div className="divide-y divide-gray-100">
                        {Object.entries(subcategories).map(([subcategory, features]) => (
                          <div key={subcategory} className="px-6 py-4">
                            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                              {subcategory}
                            </h3>
                            <div className="overflow-x-auto">
                              <table className="min-w-full table-fixed">
                                <thead>
                                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <th className="pb-2 pr-4 w-1/3">Feature</th>
                                    <th className="pb-2 pr-4 w-40">Status</th>
                                    <th className="pb-2">Notes</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                  {features.map((feature, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                      <td className="py-2 pr-4 text-sm font-medium text-gray-900 w-1/3">
                                        {feature.feature}
                                      </td>
                                      <td className="py-2 pr-4 w-40">
                                        {getStatusBadge(feature.status)}
                                      </td>
                                      <td className="py-2 text-sm text-gray-500">
                                        {feature.notes || '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Need a Feature That's Not Listed?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We're constantly expanding XLIM's capabilities. Contact us to discuss your specific requirements 
                or to request new feature support.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link href="/contact" className="btn btn-primary btn-lg">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
